import { SxProps, Typography } from '@mui/material';
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Organization } from '../../../../api/api';
import { localized, localizedInterpolation } from '../../../../i18n/i18n';
import { deleteOrganization } from '../../../../state/features/organization/operation';
import { AppState } from '../../../../state/store';
import { invalidColor } from '../../../../styles/color-constants';
import { StyledButton } from '../../../components/specific-components/buttons/styled-button';
import { ConfirmDeleteDialog } from '../../../components/specific-components/confirm-delete-dialog';
import { useRightSidebar } from '../../../components/specific-components/right-side-bar';

const localStyles: { [key: string]: SxProps } = {
  inputLine: {
    marginTop: 1,
    marginBottom: 1,
  },
  deleteButton: {
    backgroundColor: invalidColor,
  },
};

interface PropsFromParent {
  existingOrganization?: Organization;
}

export const OrganizationDeleteButton: FC<PropsFromParent> = memo(({ existingOrganization }) => {
  const dispatch = useDispatch();
  const setSidebarContent = useRightSidebar();
  const { pending } = useSelector((store: AppState) => store.organizationReducer);
  const { users } = useSelector((store: AppState) => store.usersReducer);
  const { devices } = useSelector((store: AppState) => store.devicesReducer);
  const [confirmModalState, setConfirmModalState] = useState(false);

  const activeUsersOrDevices = useMemo(() => users.length > 0 || devices.length > 0, [devices.length, users.length]);

  const openConfirmModal = useCallback(() => {
    setConfirmModalState(true);
  }, []);

  const onDeleteOrganization = useCallback(() => {
    if (!existingOrganization && activeUsersOrDevices) return;

    existingOrganization && dispatch(deleteOrganization(existingOrganization));
    setConfirmModalState(false);
    setSidebarContent(null);
  }, [activeUsersOrDevices, dispatch, existingOrganization, setSidebarContent]);

  return (
    <>
      {existingOrganization && (
        <>
          <Typography sx={{ marginTop: 10, marginBottom: -3 }}>{localized('ToBeAbleToDeleteOrganization')}</Typography>
          <StyledButton
            disabled={pending || activeUsersOrDevices}
            onClick={openConfirmModal}
            sx={localStyles.deleteButton}>
            {localizedInterpolation('DeleteInt', { area: localized('Organization').toLocaleLowerCase() })}
          </StyledButton>
          <ConfirmDeleteDialog
            open={confirmModalState}
            setOpen={setConfirmModalState}
            title={localizedInterpolation('ConfirmDeleteInt', { entityName: existingOrganization.name })}
            handleDeleteConfirm={onDeleteOrganization}
          />
        </>
      )}
    </>
  );
});
